// Block reuse helpers for warm incremental compile passes.
// Example: unchanged selector blocks keep prior rendered CSS and debug wrapping.
import { extractLeafDependencies, buildVariableSignatures } from './dependencies'
import { createDependencyHash, createLeafFingerprint } from './hash'
import { renderLeafRule } from '../renderer'
import { wrapRuleWithDebugSourceComment } from './debug'
import type { LuisRenderLeaf } from '../renderer'
import type { LuisIncrementalCompileCache } from './types'
import type { IncrementalReuseArtifacts } from './compile-types'

// Build cache reuse artifacts for a warm incremental compile pass.
export function buildIncrementalReuseArtifacts(
  leaves: LuisRenderLeaf[],
  previousCache: LuisIncrementalCompileCache | undefined,
  variables: Record<string, string>,
  classVariables: Record<string, string>,
  objectVariables: Record<string, Record<string, string>>,
  arrayVariables: Record<string, string[]>,
  sourceId: string,
  lineOffset: number,
  debugEnabled: boolean,
): IncrementalReuseArtifacts {
  const variableSignatures = buildVariableSignatures(variables, classVariables, objectVariables, arrayVariables)
  const previousByKey = new Map(previousCache?.blocks.map((block) => [block.key, block]) ?? [])
  const nextBlocks: IncrementalReuseArtifacts['nextBlocks'] = []
  const ruleOverridesByKey = new Map<string, string>()
  let reusedBlocks = 0
  // Start one warm incremental reuse scan across the current leaves.
  for (const leaf of leaves) {
    const dependencies = extractLeafDependencies(leaf)
    const dependencyHash = createDependencyHash(dependencies, variableSignatures)
    const fingerprint = createLeafFingerprint(leaf)
    const previousBlock = previousByKey.get(leaf.key)
    // Check whether the previous cached block can be reused unchanged.
    if (canReuseIncrementalBlock(previousBlock, fingerprint, dependencyHash, debugEnabled)) {
      reusedBlocks += 1
      ruleOverridesByKey.set(leaf.key, previousBlock.rule)
      nextBlocks.push({ ...previousBlock, dependencies, atRules: [...leaf.atRules] })
      continue
    }

    const nextRule = renderLeafRule(leaf)
    const nextOutputRule = wrapRuleWithDebugSourceComment(nextRule, sourceId, leaf.sourceLine, lineOffset, debugEnabled)
    ruleOverridesByKey.set(leaf.key, nextOutputRule)
    nextBlocks.push({ key: leaf.key, fingerprint, debug: debugEnabled, dependencies, dependencyHash, atRules: [...leaf.atRules], rule: nextOutputRule })
  }
  // Return rule overrides, next cache blocks, and reuse counters.
  return { nextBlocks, ruleOverridesByKey, reusedBlocks }
}

// Check whether one cached block still matches the current leaf state.
function canReuseIncrementalBlock(
  previousBlock: LuisIncrementalCompileCache['blocks'][number] | undefined,
  fingerprint: string,
  dependencyHash: string,
  debugEnabled: boolean,
): previousBlock is LuisIncrementalCompileCache['blocks'][number] {
  // Return false when no previous block exists for the current leaf key.
  if (!previousBlock) return false
  // Return true only when fingerprint, dependency hash, and debug mode all match.
  return previousBlock.fingerprint === fingerprint
    && previousBlock.debug === debugEnabled
    && previousBlock.dependencyHash === dependencyHash
}
